import { useEffect, useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import {
  createInventoryItem,
  createInventoryMovement,
  deleteInventoryItem,
  getInventoryItems,
  getInventoryMovements,
  updateInventoryItem,
  uploadInventoryImage,
} from '@/lib/api';
import { getApiBaseUrl } from '@/lib/apiBaseUrl';
import { AlertTriangle, History, PackagePlus, Pencil, Plus, RefreshCw, Trash2, Warehouse } from 'lucide-react';

const CATEGORIES = [
  { value: 'shirting', label: 'Shirting' },
  { value: 'suiting', label: 'Suiting' },
  { value: 'lining', label: 'Lining' },
  { value: 'button', label: 'Button' },
  { value: 'other', label: 'Other' },
];

const UNITS = [
  { value: 'metres', label: 'Metres' },
  { value: 'pieces', label: 'Pieces' },
];

const emptyForm = {
  sku: '',
  aliases: '',
  name: '',
  category: 'shirting',
  unit: 'metres',
  opening_qty: '',
  low_qty: '',
  colour: '',
  location: '',
  width_inches: '',
  notes: '',
  is_active: true,
};

const inventoryImageSrc = (url) => {
  if (!url) return '';
  if (/^https?:\/\//i.test(url)) return url;
  const base = getApiBaseUrl();
  if (url.startsWith('/uploads/')) return `${base}/api${url}`;
  if (url.startsWith('/')) return `${base}${url}`;
  return url;
};

const formatQty = (value, unit) => {
  const n = Number(value || 0);
  const shown = Number.isInteger(n) ? String(n) : n.toFixed(3).replace(/0+$/, '').replace(/\.$/, '');
  const suffix = unit === 'pieces' ? 'pcs' : 'm';
  return `${shown} ${suffix}`;
};

const parseAliases = (text) =>
  String(text || '')
    .split(/[\n,]/)
    .map((part) => part.trim())
    .filter(Boolean);

const toForm = (item) => ({
  sku: item.sku || '',
  aliases: (item.aliases || []).join('\n'),
  name: item.name || '',
  category: item.category || 'other',
  unit: item.unit || 'metres',
  opening_qty: '',
  low_qty: item.low_qty == null ? '' : String(item.low_qty),
  colour: item.colour || '',
  location: item.location || '',
  width_inches: item.width_inches == null ? '' : String(item.width_inches),
  notes: item.notes || '',
  is_active: item.is_active !== false,
});

const toPayload = (form, isCreate) => {
  const payload = {
    sku: form.sku,
    aliases: parseAliases(form.aliases),
    name: form.name,
    category: form.category,
    unit: form.unit,
    low_qty: form.low_qty === '' ? null : Number(form.low_qty),
    colour: form.colour,
    location: form.location,
    width_inches: form.width_inches === '' ? null : Number(form.width_inches),
    notes: form.notes,
    is_active: form.is_active,
  };
  if (isCreate) {
    payload.opening_qty = form.opening_qty === '' ? 0 : Number(form.opening_qty);
  }
  return payload;
};

export default function InventoryPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [activeFilter, setActiveFilter] = useState('true');
  const [lowOnly, setLowOnly] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [imageFile, setImageFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [stockItem, setStockItem] = useState(null);
  const [stockType, setStockType] = useState('receipt');
  const [stockQty, setStockQty] = useState('');
  const [stockNewOnHand, setStockNewOnHand] = useState('');
  const [stockReason, setStockReason] = useState('');
  const [historyItem, setHistoryItem] = useState(null);
  const [movements, setMovements] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const loadItems = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (category !== 'all') params.category = category;
      if (activeFilter !== 'all') params.active = activeFilter;
      if (lowOnly) params.low_only = 'true';
      const response = await getInventoryItems(params);
      setItems(response.data || []);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || 'Failed to load inventory');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [category, activeFilter, lowOnly]);

  const openCreate = () => {
    setEditingItem(null);
    setForm(emptyForm);
    setImageFile(null);
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm(toForm(item));
    setImageFile(null);
    setFormOpen(true);
  };

  const handleSave = async () => {
    if (!form.sku.trim() || !form.name.trim()) {
      toast.error('SKU and name are required');
      return;
    }
    setSaving(true);
    try {
      const isCreate = !editingItem;
      let saved = editingItem;
      if (isCreate) {
        const response = await createInventoryItem(toPayload(form, true));
        saved = response.data;
      } else {
        const response = await updateInventoryItem(editingItem.inventory_item_id, toPayload(form, false));
        saved = response.data;
      }
      if (imageFile && saved?.inventory_item_id) {
        await uploadInventoryImage(saved.inventory_item_id, imageFile);
      }
      toast.success(isCreate ? 'Inventory item created' : 'Inventory item updated');
      setFormOpen(false);
      await loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save inventory item');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteInventoryItem(deleteTarget.inventory_item_id);
      toast.success('Inventory item deleted');
      setDeleteTarget(null);
      await loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete inventory item');
    }
  };

  const openStock = (item, type = 'receipt') => {
    setStockItem(item);
    setStockType(type);
    setStockQty('');
    setStockNewOnHand(item.on_hand == null ? '' : String(item.on_hand));
    setStockReason('');
  };

  const handleStock = async () => {
    if (!stockItem) return;
    const payload = { movement_type: stockType, reason: stockReason };
    if (stockType === 'stocktake') {
      payload.new_on_hand = Number(stockNewOnHand);
    } else if (stockType === 'adjustment') {
      payload.qty = Number(stockQty);
    } else {
      payload.qty = Number(stockQty);
    }
    try {
      await createInventoryMovement(stockItem.inventory_item_id, payload);
      toast.success('Stock updated');
      setStockItem(null);
      await loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stock');
    }
  };

  const openHistory = async (item) => {
    setHistoryItem(item);
    setHistoryLoading(true);
    try {
      const response = await getInventoryMovements(item.inventory_item_id);
      setMovements(response.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load movement history');
      setMovements([]);
    } finally {
      setHistoryLoading(false);
    }
  };

  const summary = useMemo(() => {
    const lowCount = items.filter((item) => item.is_low).length;
    const skuCount = items.length;
    return { skuCount, lowCount };
  }, [items]);

  return (
    <div className="space-y-4 p-4 md:p-6" data-testid="inventory-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <Warehouse className="h-6 w-6" />
            Inventory
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
            House-stock shirtings, suitings, linings, and buttons. On-hand is physical stock. Reserved stays
            reserved until a garment is past cutting. Available is on-hand minus reserved.
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadItems} data-testid="inventory-refresh-button">
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
          <Button onClick={openCreate} data-testid="inventory-add-button">
            <Plus className="mr-2 h-4 w-4" />
            Add SKU
          </Button>
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>SKUs in view</CardDescription>
            <CardTitle>{summary.skuCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Low available</CardDescription>
            <CardTitle className={summary.lowCount ? 'text-amber-700' : undefined}>{summary.lowCount}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Reservations</CardDescription>
            <CardTitle className="text-base font-medium text-muted-foreground">Not wired to orders yet</CardTitle>
          </CardHeader>
        </Card>
      </div>

      <Card>
        <CardContent className="flex flex-wrap items-end gap-3 pt-6">
          <div className="min-w-[220px] flex-1">
            <Label htmlFor="inventory-search">Search</Label>
            <Input
              id="inventory-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadItems();
              }}
              placeholder="SKU, name, alias, colour…"
              data-testid="inventory-search-input"
            />
          </div>
          <div className="w-40">
            <Label>Category</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger data-testid="inventory-category-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                {CATEGORIES.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="w-40">
            <Label>Status</Label>
            <Select value={activeFilter} onValueChange={setActiveFilter}>
              <SelectTrigger data-testid="inventory-active-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="true">Active</SelectItem>
                <SelectItem value="false">Inactive</SelectItem>
                <SelectItem value="all">All</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button
            variant={lowOnly ? 'default' : 'outline'}
            onClick={() => setLowOnly((value) => !value)}
            data-testid="inventory-low-filter"
          >
            <AlertTriangle className="mr-2 h-4 w-4" />
            Low only
          </Button>
          <Button variant="secondary" onClick={loadItems}>Search</Button>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="pt-6">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading inventory…</p>
          ) : items.length === 0 ? (
            <p className="text-sm text-muted-foreground">No house-stock SKUs yet. Add a shirting, suiting, lining, or button SKU to start.</p>
          ) : (
            <Table data-testid="inventory-table">
              <TableHeader>
                <TableRow>
                  <TableHead>SKU</TableHead>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead className="text-right">On hand</TableHead>
                  <TableHead className="text-right">Reserved</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.inventory_item_id} data-testid={`inventory-row-${item.sku}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {item.image_url ? (
                          <img
                            src={inventoryImageSrc(item.image_url)}
                            alt=""
                            className="h-12 w-12 rounded-md object-cover border"
                          />
                        ) : (
                          <div className="flex h-12 w-12 items-center justify-center rounded-md border bg-slate-50 text-slate-400">
                            <Warehouse className="h-4 w-4" />
                          </div>
                        )}
                        <div>
                          <div className="font-medium">{item.sku}</div>
                          {item.aliases?.length > 0 && (
                            <div className="text-xs text-muted-foreground">{item.aliases.join(', ')}</div>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div>{item.name}</div>
                      <div className="text-xs text-muted-foreground">
                        {[item.colour, item.location].filter(Boolean).join(' · ') || '—'}
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{item.category}</TableCell>
                    <TableCell className="text-right">{formatQty(item.on_hand, item.unit)}</TableCell>
                    <TableCell className="text-right">{formatQty(item.reserved, item.unit)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {formatQty(item.available, item.unit)}
                        {item.is_low && (
                          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800">Low</Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="outline" onClick={() => openStock(item, 'receipt')} title="Receive stock">
                          <PackagePlus className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openHistory(item)} title="Movement history">
                          <History className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openEdit(item)} title="Edit">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDeleteTarget(item)} title="Delete">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="inventory-item-dialog">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit inventory SKU' : 'Add inventory SKU'}</DialogTitle>
            <DialogDescription>
              Master data only. Change on-hand with a receipt, cut, stocktake, or adjustment so every change is audited.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-1">
              <Label htmlFor="inv-sku">SKU</Label>
              <Input id="inv-sku" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="inv-name">Name</Label>
              <Input id="inv-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div>
              <Label>Category</Label>
              <Select value={form.category} onValueChange={(value) => setForm({ ...form, category: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Unit</Label>
              <Select value={form.unit} onValueChange={(value) => setForm({ ...form, unit: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {UNITS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {!editingItem && (
              <div>
                <Label htmlFor="inv-opening">Opening on-hand</Label>
                <Input id="inv-opening" type="number" min="0" step="0.001" value={form.opening_qty} onChange={(e) => setForm({ ...form, opening_qty: e.target.value })} />
              </div>
            )}
            <div>
              <Label htmlFor="inv-low">Low available warning</Label>
              <Input id="inv-low" type="number" min="0" step="0.001" value={form.low_qty} onChange={(e) => setForm({ ...form, low_qty: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="inv-colour">Colour</Label>
              <Input id="inv-colour" value={form.colour} onChange={(e) => setForm({ ...form, colour: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="inv-location">Location / factory</Label>
              <Input id="inv-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div>
              <Label htmlFor="inv-width">Width (inches)</Label>
              <Input id="inv-width" type="number" min="0" step="0.1" value={form.width_inches} onChange={(e) => setForm({ ...form, width_inches: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="inv-aliases">Aliases (one per line)</Label>
              <Textarea id="inv-aliases" rows={3} value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} placeholder="Old mill codes, supplier SKUs…" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="inv-notes">Notes</Label>
              <Textarea id="inv-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="inv-image">Photo</Label>
              <Input id="inv-image" type="file" accept="image/*" onChange={(e) => setImageFile(e.target.files?.[0] || null)} />
            </div>
            <label className="flex items-center gap-2 text-sm sm:col-span-2">
              <Checkbox
                checked={form.is_active}
                onCheckedChange={(checked) => setForm({ ...form, is_active: Boolean(checked) })}
              />
              Active SKU
            </label>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="inventory-save-button">
              {saving ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(stockItem)} onOpenChange={(open) => { if (!open) setStockItem(null); }}>
        <DialogContent data-testid="inventory-stock-dialog">
          <DialogHeader>
            <DialogTitle>Update on-hand · {stockItem?.sku}</DialogTitle>
            <DialogDescription>
              Current on-hand {stockItem ? formatQty(stockItem.on_hand, stockItem.unit) : ''}. This does not change reserved quantity.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Movement</Label>
              <Select value={stockType} onValueChange={setStockType}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="receipt">Receipt (add)</SelectItem>
                  <SelectItem value="cut">Cut (consume)</SelectItem>
                  <SelectItem value="stocktake">Stocktake (set counted qty)</SelectItem>
                  <SelectItem value="adjustment">Adjustment (+/−)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {stockType === 'stocktake' ? (
              <div>
                <Label>Counted on-hand</Label>
                <Input type="number" step="0.001" value={stockNewOnHand} onChange={(e) => setStockNewOnHand(e.target.value)} />
              </div>
            ) : (
              <div>
                <Label>{stockType === 'adjustment' ? 'Quantity change (use negative to reduce)' : 'Quantity'}</Label>
                <Input type="number" step="0.001" value={stockQty} onChange={(e) => setStockQty(e.target.value)} />
              </div>
            )}
            <div>
              <Label>Reason {(stockType === 'adjustment' || stockType === 'stocktake') ? '(required)' : '(optional)'}</Label>
              <Textarea rows={2} value={stockReason} onChange={(e) => setStockReason(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStockItem(null)}>Cancel</Button>
            <Button onClick={handleStock} data-testid="inventory-stock-save">Apply</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(historyItem)} onOpenChange={(open) => { if (!open) setHistoryItem(null); }}>
        <DialogContent className="sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Movements · {historyItem?.sku}</DialogTitle>
            <DialogDescription>Every on-hand change is recorded. There is no silent edit of quantity.</DialogDescription>
          </DialogHeader>
          {historyLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : movements.length === 0 ? (
            <p className="text-sm text-muted-foreground">No movements yet.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Qty</TableHead>
                  <TableHead className="text-right">After</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {movements.map((row) => (
                  <TableRow key={row.movement_id}>
                    <TableCell className="whitespace-nowrap text-xs">
                      {row.created_at ? new Date(row.created_at).toLocaleString() : '—'}
                    </TableCell>
                    <TableCell className="capitalize">{row.movement_type}</TableCell>
                    <TableCell className="text-right">{row.qty}</TableCell>
                    <TableCell className="text-right">{row.on_hand_after}</TableCell>
                    <TableCell className="text-xs">{row.reason || '—'}{row.user_email ? ` · ${row.user_email}` : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleteTarget?.sku}?</AlertDialogTitle>
            <AlertDialogDescription>
              This removes the SKU and its movement history. Items with open reservations cannot be deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
